import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  CanActivateChildFn,
  CanActivateFn,
  Params,
  Router,
  UrlTree,
} from '@angular/router';
import { KeycloakAdminService } from '../keycloak/keycloak.service';
import { environment } from '../../../environments/environment';
import {
  RETURN_URL_PARAM,
  SIGN_IN_REASON_PARAM,
  isSignInReason,
  returnUrlParam,
  safeReturnUrl,
} from './return-url';

/**
 * Console routes need a stored token that has not expired. Without one the
 * visitor goes to sign-in with the page they asked for as `returnUrl`; an
 * expired token is cleared first and adds `reason=expired`, so sign-in can
 * say why instead of the shell flashing up and bouncing.
 */
function requireSession(url: string): boolean | UrlTree {
  // The server has no token storage. Render nothing for console routes there
  // and let the browser decide once it boots.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return false;
  }

  const kcService = inject(KeycloakAdminService);
  if (kcService.hasValidSession) {
    return true;
  }

  const queryParams: Params = {};
  if (kcService.isSessionExpired) {
    kcService.storageService.clearState();
    queryParams[SIGN_IN_REASON_PARAM] = 'expired';
  }
  const back = returnUrlParam(url);
  if (back) {
    queryParams[RETURN_URL_PARAM] = back;
  }
  return inject(Router).createUrlTree(['/'], { queryParams });
}

export const canActivateChildAuthGuard: CanActivateChildFn = (_route, state) =>
  requireSession(state.url);

export const canActivateGuard: CanActivateFn = (_route, state) =>
  requireSession(state.url);

/**
 * Where a visitor of the sign-in page should go instead of seeing the form:
 * their return URL (or the dashboard) when they already hold a valid session,
 * otherwise null. Mock mode (devBypassAuth) always seeds a session, so it
 * keeps the form reachable there for previews; a sign-in `reason` (session
 * ended) always shows the form.
 */
export function signedInRedirectUrl(
  kcService: KeycloakAdminService,
  queryParams: Params
): string | null {
  const { devBypassAuth } = environment as { devBypassAuth?: boolean };
  if (devBypassAuth) return null;
  if (isSignInReason(queryParams[SIGN_IN_REASON_PARAM])) return null;
  if (!kcService.hasValidSession) return null;
  return safeReturnUrl(queryParams[RETURN_URL_PARAM]);
}

/** For the '' (sign-in) route: skip the form when already signed in. */
export const redirectSignedInGuard: CanActivateFn = (route) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }
  const target = signedInRedirectUrl(
    inject(KeycloakAdminService),
    route.queryParams
  );
  return target ? inject(Router).parseUrl(target) : true;
};
